import {
    HttpClient,
    HttpContext,
    HttpHeaders,
    HttpParams
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiResponse } from './api.models';

import { AppService } from '@/service/app.service';


export interface HttpOptions {
    headers?: HttpHeaders | Record<string, string | string[]>;
    context?: HttpContext;
    observe?: 'body';
    params?: HttpParams | Record<string, string | number | boolean | readonly (string | number | boolean)[]>;
    reportProgress?: boolean;
    responseType?: 'json';
    withCredentials?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class ApiService {
    apiUrl!: string;

    constructor(
        private appService: AppService,
        private http: HttpClient
    ) {
        this.apiUrl = this.appService.environment.apiUrl;
    }

    get<T>(path: string, params?: HttpParams | Record<string, any>, options: HttpOptions = {}): Observable<ApiResponse<T>> {
        if (!(params instanceof HttpParams)) {
            params = this.build(params);
        }
        options.params = params;
        return this.http.get<ApiResponse<T>>(`${this.apiUrl}${path}`, options);
    }

    post<T>(path: string, body: Record<string, any> | FormData = {}, options: HttpOptions = {}): Observable<ApiResponse<T>> {
        if (body instanceof FormData) {
            options.reportProgress = true;
        }
        return this.http.post<ApiResponse<T>>(`${this.apiUrl}${path}`, body, options);
    }

    put<T>(path: string, body?: Record<string, any> | FormData, options: HttpOptions = {}): Observable<ApiResponse<T>> {
        if (body instanceof FormData) {
            options.reportProgress = true;
        }
        return this.http.put<ApiResponse<T>>(`${this.apiUrl}${path}`, body, options);
    }

    patch<T>(path: string, body: Record<string, any> | FormData, options: HttpOptions = {}): Observable<ApiResponse<T>> {
        if (body instanceof FormData) {
            options.reportProgress = true;
        }
        return this.http.patch<ApiResponse<T>>(`${this.apiUrl}${path}`, body, options);
    }

    delete<T>(path: string, options: HttpOptions = {}) {
        return this.http.delete<ApiResponse<T>>(`${this.apiUrl}${path}`, options);
    }

    build(params: Record<string, any> = {}): HttpParams {
        let httpParams: HttpParams = new HttpParams();
        for (const key of Object.keys(params)) {
            if (key.startsWith('-')) {
                continue;
            }
            const v = params[key];
            if (Array.isArray(v)) {
                v.forEach((v, i) => {
                    httpParams = httpParams.append(`${key}[${i}]`, v);
                });
            } else if (v instanceof Date && !isNaN(v.getTime())) {
                httpParams = httpParams.set(key, Math.floor(v.getTime() / 1000));
            } else {
                httpParams = httpParams.set(key, v);
            }
        }
        return httpParams;
    }

    getBlob(path: string, params?: Record<string, any>, options: HttpOptions = {}): Observable<Blob> {
        if (!(params instanceof HttpParams)) {
            params = this.build(params);
        }

        return this.http.get(`${this.apiUrl}${path}`, {
            ...options,
            params,
            responseType: 'blob' as const
        });
    }

    postBlob(
        path: string,
        body?: Record<string, any>,
        options: HttpOptions = {}
    ): Observable<Blob> {
        return this.http.post(
            `${this.apiUrl}${path}`,
            body ?? {},
            {
                ...options,
                responseType: 'blob' as const
            }
        );
    }

}
